import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { PayrollTypeFullEnum } from '@shared-enums/time.enum';
import { addDecimals } from '@shared-helpers/string-number-helpers';

import { IWeeklyTotal } from '../../../interfaces/internal-interfaces';
import { Employee } from '../../../scripts/employee';
import { timeEvalSummary } from '../../../scripts/table-configs/payroll-tables.config';

@Component({
  selector: 'app-modal-content-time-eval',
  templateUrl: './modal-content-time-eval.component.html',
  styleUrl: './modal-content-time-eval.component.css',
  standalone: false,
})
export class ModalContentTimeEvalComponent implements OnInit {
  @Input() employee: Employee;

  @Output() public readonly closed = new EventEmitter<boolean>();

  timesheet: IIterablePayroll[];
  timeEval: IIterablePayroll[];
  weDate: string;
  otType: string;
  workScheduleRule: string;
  unpaidHours = '0';
  containsUnpaid = false;
  timesheetTotalPaid = '0';
  timeEvalTotalPaid = '0';

  hasAutomationOverride: boolean;
  private shouldAllowOverride = false;

  config = timeEvalSummary;
  elementsDisplay = {
    hideButton: false,
    hideTimesheetWindow: false,
    hidePayrollAutomationWindow: false,
    multiWindow: true,
  };

  ngOnInit(): void {
    if (!this.employee) throw Error('You must supply an employee in order to use this component.');
    this.timesheet = this.makeIterable(this.employee.payrollTotals);
    this.timeEval = this.makeIterable(this.employee.payrollSimulator.payrollSimulation);
    this.timesheetTotalPaid = this.getTotal(this.timesheet);
    this.timeEvalTotalPaid = this.getTotal(this.timeEval);
    this.hasAutomationOverride = this.employee.payrollSimulator.isPayrollAutomationOverridden;
    this.weDate = this.employee.datahub.weDate.getHumanReadableDateString();
    this.otType = this.employee.TimeMngtText;
    if (this.employee.TimeMngtCode === '1') {
      // 1 = pay as recorded employee
      this.hasAutomationOverride = true;
      this.elementsDisplay.hidePayrollAutomationWindow = true;
      this.elementsDisplay.hideButton = true;
      this.elementsDisplay.multiWindow = false;
    }
    if (this.employee.TimeMngtCode === '9') {
      // 9 = salaried employee
      this.elementsDisplay.hideTimesheetWindow = true;
      this.elementsDisplay.hideButton = true;
      this.elementsDisplay.multiWindow = false;
    }
    this.workScheduleRule = this.employee.WorkScheduleRule;
    this.shouldAllowOverride = this.employee.TimeMngtCode !== '9';
    this.unpaidHours = this.employee.payrollTotals.u.total;
    this.containsUnpaid = Number(this.unpaidHours) > 0;
  }

  private makeIterable(weeklyTotal: IWeeklyTotal): IIterablePayroll[] {
    if (this.employee.TimeMngtCode === '9') {
      // 9 = salaried employee
      weeklyTotal.s.mon = '8';
      weeklyTotal.s.tue = '8';
      weeklyTotal.s.wed = '8';
      weeklyTotal.s.thu = '8';
      weeklyTotal.s.fri = '8';
      weeklyTotal.s.total = '40';
    }

    return (Object.keys(weeklyTotal) as Array<keyof IWeeklyTotal>).map((key) => {
      const element = weeklyTotal[key] as IIterablePayroll;
      element.type = this.parseKey(key);
      element.shouldShow = true;
      if (key === PayrollTypeFullEnum.Unpaid) element.shouldShow = false;
      if (key === PayrollTypeFullEnum.Holiday) element.shouldShow = false; //delete this once we want to show holiday
      if (Number(element.total)) element.shouldHighlight = true;
      return element;
    });
  }

  private parseKey(key: keyof IWeeklyTotal): string {
    if (key === PayrollTypeFullEnum.Straight) return 'ST';
    if (key === PayrollTypeFullEnum.Overtime) return 'OT';
    if (key === PayrollTypeFullEnum.Double) return 'DT';
    if (key === PayrollTypeFullEnum.Holiday) return 'Holiday';
    if (key === PayrollTypeFullEnum.Unpaid) return 'Unpaid';
    return '';
  }

  private getTotal(weeklyTotal: IIterablePayroll[]): string {
    let total = '0';
    weeklyTotal
      .filter((x) => !!x.shouldShow)
      .forEach((x) => {
        total = addDecimals(total, x.total);
      });
    return total;
  }

  toggleAutomationOverride(shouldOverride: boolean) {
    if (!this.shouldAllowOverride && shouldOverride) {
      this.employee.datahub.messageService.warning('Salaried Employees cannot use payroll override.');
      return;
    }
    this.hasAutomationOverride = shouldOverride;
  }

  accept() {
    this.closed.emit(this.hasAutomationOverride);
  }
}

export interface IIterablePayroll {
  type: string;
  sun: string;
  mon: string;
  tue: string;
  wed: string;
  thu: string;
  fri: string;
  sat: string;
  total: string;
  shouldShow: boolean;
  shouldHighlight: boolean;
}
